import { useState, useEffect, useRef } from "react";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ChevronDown, Play, Headphones, FileText, Share2, ExternalLink, Mail, Phone, Globe, Loader2, Check, AlertTriangle } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function EmptyChair() {
  useSEO("empty-chair");
  const [d, setD] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [faq, setFaq] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);

  useEffect(() => {
    Promise.all([
      http.get("/empty-chair/public/settings"),
      http.get("/empty-chair/public/episodes"),
      http.get("/empty-chair/public/faq"),
      http.get("/empty-chair/public/testimonials"),
    ]).then(([s, e, f, t]) => {
      setD(s.data); setEpisodes(e.data); setFaq(f.data); setTestimonials(t.data);
    }).catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !d) return <Layout><div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="text-[#D4AF37] animate-spin" size={32} /></div></Layout>;

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });
  const featured = episodes.find(ep => ep.featured) || episodes[0];
  const disabled = d.disabled_sections || [];
  const show = (key) => !disabled.includes(key);

  return (
    <Layout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-ec-h { font-family: 'Cormorant Garamond', serif; }
        .font-ec-b { font-family: 'DM Sans', sans-serif; }
      `}</style>

      {show("hero") && <Hero d={d} scrollToForm={scrollToForm} />}
      {show("what") && <WhatIs d={d} />}
      {show("why") && <WhyChair d={d} />}
      {show("host") && <MeetHost d={d} />}
      {show("who") && <WhoShould d={d} scrollToForm={scrollToForm} />}
      {show("steps") && <GuestSteps d={d} />}
      {show("rights") && <GuestRights d={d} />}
      {show("episodes") && <EpisodeLibrary episodes={episodes} d={d} />}
      {show("featured") && featured && <FeaturedEpisode ep={featured} />}
      {show("solo") && <SoloEpisodes d={d} />}
      {show("guest_form") && <div ref={formRef}><GuestRequestForm d={d} /></div>}
      {show("faq") && <FaqSection faq={faq} />}
      {show("testimonials") && <Testimonials testimonials={testimonials} />}
      {show("share_response") && <ShareResponse episodes={episodes} />}
      {show("resources") && <Resources d={d} />}
      {show("newsletter") && <Newsletter d={d} />}
      {show("safety") && <SafetyNotice d={d} />}
      {show("contact") && <Contact d={d} />}
      {show("media_inquiry") && <MediaInquiry d={d} />}
      {show("footer_cta") && <FooterCTA d={d} scrollToForm={scrollToForm} />}
    </Layout>
  );
}

/* ═══ HERO ═══ */
function Hero({ d, scrollToForm }) {
  return (
    <section className="relative min-h-screen bg-[#0A0A0A] flex items-center justify-center overflow-hidden" data-testid="ec-hero">
      {d.hero_bg_image && <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${resolveImage(d.hero_bg_image)})` }} />}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/60 to-[#0A0A0A]" />
      <motion.div initial="hidden" animate="visible" variants={stagger} className="relative z-10 text-center px-6 max-w-4xl mx-auto py-32">
        {d.hero_artwork && (
          <motion.img variants={fadeUp} src={resolveImage(d.hero_artwork)} alt="The Empty Chair Podcast" className="w-64 h-64 md:w-80 md:h-80 mx-auto mb-10 rounded-2xl object-cover shadow-[0_0_60px_rgba(212,175,55,0.15)]" />
        )}
        <motion.h1 variants={fadeUp} className="font-ec-h text-4xl sm:text-5xl lg:text-7xl font-light text-[#F5F5F0] tracking-wide mb-4">{d.hero_headline}</motion.h1>
        <motion.p variants={fadeUp} className="font-ec-b text-sm tracking-[0.3em] uppercase text-[#D4AF37] mb-8">{d.hero_tagline}</motion.p>
        <motion.p variants={fadeUp} className="font-ec-b text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto whitespace-pre-line mb-10">{d.hero_copy}</motion.p>
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <a href="#episodes" className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 rounded-full font-ec-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all">{d.hero_primary_btn}</a>
          <button onClick={scrollToForm} className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 rounded-full font-ec-b tracking-wide uppercase text-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">{d.hero_secondary_btn}</button>
        </motion.div>
        <motion.p variants={fadeUp} className="font-ec-b text-xs text-zinc-600">{d.hero_hosted_by}</motion.p>
      </motion.div>
    </section>
  );
}

/* ═══ WHAT IS ═══ */
function WhatIs({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-what">
      <div className="max-w-4xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0]">{d.what_heading}</h2>
        </motion.div>
        <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-b text-base text-zinc-400 leading-relaxed whitespace-pre-line mb-16 max-w-3xl mx-auto">{d.what_copy}</motion.p>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
          {(d.what_cards || []).map((c, i) => (
            <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 text-center backdrop-blur-sm">
              <h3 className="font-ec-h text-xl text-[#D4AF37] mb-3">{c.title}</h3>
              <p className="font-ec-b text-sm text-zinc-400 leading-relaxed">{c.copy}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══ WHY ═══ */
function WhyChair({ d }) {
  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-why">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-8">{d.why_heading}</h2>
          <p className="font-ec-b text-base text-zinc-400 mb-10">{d.why_copy}</p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-left max-w-xl mx-auto mb-12">
          {(d.why_items || []).map((item, i) => (
            <motion.p key={i} variants={fadeUp} className="font-ec-h text-lg md:text-xl text-zinc-200 italic py-2 border-b border-white/[0.04] last:border-0">{item}</motion.p>
          ))}
        </motion.div>
        <motion.p initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-2xl text-[#D4AF37] italic">{d.why_closing}</motion.p>
      </div>
    </section>
  );
}

/* ═══ HOST ═══ */
function MeetHost({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-host">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          {d.host_image ? (
            <img src={resolveImage(d.host_image)} alt="Mickey Trivett" className="w-full max-w-sm mx-auto rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
          ) : (
            <div className="w-full max-w-sm mx-auto aspect-[3/4] rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center">
              <p className="font-ec-b text-zinc-600 text-sm">Photo coming soon</p>
            </div>
          )}
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-ec-h text-3xl md:text-4xl font-light text-[#F5F5F0] mb-6">{d.host_heading}</h2>
          <p className="font-ec-b text-base text-zinc-400 leading-relaxed whitespace-pre-line mb-8">{d.host_copy}</p>
          <blockquote className="border-l-2 border-[#D4AF37] pl-6 mb-8">
            <p className="font-ec-h text-xl text-[#F5F5F0] italic whitespace-pre-line">{d.host_quote}</p>
          </blockquote>
          <Link to={d.host_btn_link || "/mirror-conversations"} className="inline-block border border-[#D4AF37] text-[#D4AF37] px-6 py-3 rounded-full font-ec-b text-sm tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">{d.host_btn}</Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══ WHO SHOULD ═══ */
function WhoShould({ d, scrollToForm }) {
  const [active, setActive] = useState(0);
  const tabs = d.who_tabs || [];
  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-who">
      <div className="max-w-4xl mx-auto">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] text-center mb-16">{d.who_heading}</motion.h2>
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {tabs.map((t, i) => (
            <button key={i} onClick={() => setActive(i)} className={`px-4 py-2.5 rounded-full font-ec-b text-xs tracking-wide transition-all ${active === i ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500 hover:border-[#D4AF37]/30"}`} data-testid={`who-tab-${i}`}>{t.title}</button>
          ))}
        </div>
        {tabs[active] && (
          <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 md:p-12 text-center max-w-2xl mx-auto">
            <h3 className="font-ec-h text-2xl text-[#F5F5F0] mb-4">{tabs[active].title}</h3>
            <p className="font-ec-b text-base text-zinc-400 leading-relaxed mb-8">{tabs[active].copy}</p>
            <button onClick={scrollToForm} className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3.5 rounded-full font-ec-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all">Request to Take the Chair</button>
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ═══ STEPS ═══ */
function GuestSteps({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-steps">
      <div className="max-w-3xl mx-auto">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] text-center mb-16">{d.steps_heading}</motion.h2>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-0">
          {(d.steps || []).map((s, i) => (
            <motion.div key={i} variants={fadeUp} className="flex gap-6 pb-10 last:pb-0">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                  <span className="font-ec-b text-sm text-[#D4AF37] font-medium">{i + 1}</span>
                </div>
                {i < (d.steps || []).length - 1 && <div className="w-px flex-1 bg-white/[0.06] mt-2" />}
              </div>
              <div className="pt-1.5">
                <h3 className="font-ec-h text-xl text-[#F5F5F0] mb-2">{s.title}</h3>
                <p className="font-ec-b text-sm text-zinc-400 leading-relaxed">{s.copy}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
        {d.steps_note && <p className="font-ec-b text-xs text-zinc-600 text-center mt-8 italic">{d.steps_note}</p>}
      </div>
    </section>
  );
}

/* ═══ RIGHTS ═══ */
function GuestRights({ d }) {
  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-rights">
      <div className="max-w-3xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
          <h2 className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-4">{d.rights_heading}</h2>
          <p className="font-ec-b text-base text-zinc-400">{d.rights_copy}</p>
        </motion.div>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8 mb-8">
          <p className="font-ec-b text-sm text-zinc-400 mb-4">Guests may:</p>
          <ul className="space-y-3">
            {(d.rights_items || []).map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check size={14} className="text-[#D4AF37] mt-1 shrink-0" />
                <span className="font-ec-b text-sm text-zinc-300">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        {d.rights_clarification && <p className="font-ec-b text-xs text-zinc-500 mb-6 italic">{d.rights_clarification}</p>}
        {d.rights_safety && (
          <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.03] p-5 flex gap-3">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="font-ec-b text-xs text-zinc-400 leading-relaxed">{d.rights_safety}</p>
          </div>
        )}
      </div>
    </section>
  );
}

/* ═══ EPISODE LIBRARY ═══ */
function EpisodeLibrary({ episodes, d }) {
  const [filter, setFilter] = useState("All Episodes");
  const cats = d.episode_categories || ["All Episodes"];
  const filtered = filter === "All Episodes" ? episodes : episodes.filter(ep => (ep.tags || []).includes(filter));
  return (
    <section id="episodes" className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-episodes">
      <div className="max-w-5xl mx-auto">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] text-center mb-12">{d.episodes_heading}</motion.h2>
        <div className="flex flex-wrap gap-2 justify-center mb-12">
          {cats.map(c => (
            <button key={c} onClick={() => setFilter(c)} className={`px-4 py-2 rounded-full font-ec-b text-xs tracking-wide transition-all ${filter === c ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500 hover:border-[#D4AF37]/30"}`}>{c}</button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Headphones size={40} className="text-zinc-700 mx-auto mb-4" />
            <p className="font-ec-h text-xl text-zinc-600">Episodes coming soon.</p>
            <p className="font-ec-b text-sm text-zinc-700 mt-2">Check back for new conversations.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(ep => <EpisodeCard key={ep.id} ep={ep} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function EpisodeCard({ ep }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden group" data-testid={`episode-${ep.id}`}>
      {ep.thumbnail ? (
        <img src={resolveImage(ep.thumbnail)} alt={ep.title} className="w-full aspect-video object-cover" />
      ) : (
        <div className="w-full aspect-video bg-[#1A1A1A] flex items-center justify-center"><Headphones size={32} className="text-zinc-700" /></div>
      )}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          {ep.episode_number > 0 && <span className="font-ec-b text-[10px] text-[#D4AF37] tracking-wider">EP {ep.episode_number}</span>}
          {ep.publication_date && <span className="font-ec-b text-[10px] text-zinc-600">{ep.publication_date}</span>}
        </div>
        {ep.guest_name && <p className="font-ec-b text-xs text-zinc-500 mb-1">{ep.guest_name}</p>}
        <h3 className="font-ec-h text-lg text-[#F5F5F0] mb-2">{ep.title}</h3>
        <p className="font-ec-b text-xs text-zinc-400 line-clamp-2 mb-4">{ep.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {(ep.tags || []).map(t => <span key={t} className="font-ec-b text-[9px] text-zinc-500 bg-white/[0.04] px-2 py-0.5 rounded-full">{t}</span>)}
        </div>
        <div className="flex gap-2">
          {ep.youtube_url && <a href={ep.youtube_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#D4AF37] font-ec-b text-xs hover:underline"><Play size={12} /> Watch</a>}
          {ep.spotify_url && <a href={ep.spotify_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-zinc-400 font-ec-b text-xs hover:underline"><Headphones size={12} /> Listen</a>}
          {ep.transcript_url && <a href={ep.transcript_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-zinc-400 font-ec-b text-xs hover:underline"><FileText size={12} /> Transcript</a>}
        </div>
      </div>
    </div>
  );
}

/* ═══ FEATURED ═══ */
function FeaturedEpisode({ ep }) {
  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-featured">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          {ep.thumbnail ? (
            <img src={resolveImage(ep.thumbnail)} alt={ep.title} className="w-full rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
          ) : (
            <div className="w-full aspect-video bg-[#1A1A1A] rounded-2xl flex items-center justify-center"><Play size={48} className="text-zinc-700" /></div>
          )}
          <div>
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Featured Episode</p>
            {ep.guest_name && <p className="font-ec-b text-sm text-zinc-500 mb-1">{ep.guest_name}</p>}
            <h3 className="font-ec-h text-3xl text-[#F5F5F0] mb-4">{ep.title}</h3>
            <p className="font-ec-b text-base text-zinc-400 leading-relaxed mb-8">{ep.description}</p>
            <div className="flex flex-wrap gap-3">
              {ep.youtube_url && <a href={ep.youtube_url} target="_blank" rel="noreferrer" className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-ec-b text-sm tracking-wide uppercase hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all flex items-center gap-2"><Play size={14} /> Watch</a>}
              {ep.spotify_url && <a href={ep.spotify_url} target="_blank" rel="noreferrer" className="border border-zinc-700 text-zinc-300 px-6 py-3 rounded-full font-ec-b text-sm hover:border-zinc-500 transition-all flex items-center gap-2"><Headphones size={14} /> Listen</a>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ SOLO ═══ */
function SoloEpisodes({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-solo">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-8">{d.solo_heading}</h2>
          <p className="font-ec-b text-base text-zinc-400 leading-relaxed whitespace-pre-line mb-10">{d.solo_copy}</p>
        </motion.div>
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {(d.solo_examples || []).map((ex, i) => (
            <span key={i} className="font-ec-h text-sm text-zinc-300 italic bg-white/[0.03] border border-white/[0.06] px-4 py-2 rounded-full">{ex}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══ GUEST REQUEST FORM ═══ */
function GuestRequestForm({ d }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCrisisMsg, setShowCrisisMsg] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, val) => {
    const arr = form[k] || [];
    u(k, arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);
  };
  const toggleAgreement = (k) => {
    const ag = form.agreements || {};
    u("agreements", { ...ag, [k]: !ag[k] });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await http.post("/empty-chair/public/guest-request", form);
      setSubmitted(true);
      toast.success("Request submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Please complete all required fields.");
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-form-success">
        <div className="max-w-2xl mx-auto text-center">
          <Check size={48} className="text-[#D4AF37] mx-auto mb-6" />
          <h2 className="font-ec-h text-3xl text-[#F5F5F0] mb-4">Thank you for sharing part of your story.</h2>
          <p className="font-ec-b text-base text-zinc-400 leading-relaxed">Your request has been received. If your story appears to be a good fit for The Empty Chair™, someone will contact you using the information provided.</p>
          <p className="font-ec-b text-sm text-zinc-500 mt-4 italic">Not every submission can become an episode, but every story matters.</p>
        </div>
      </section>
    );
  }

  const TOPICS = ["Grief and loss","Starting over","Anxiety or emotional wellness","Recovery","Relationships","Family","Parenting","Faith","Business","Leadership","Community impact","Failure","Resilience","Health journey","Identity","Purpose","Other"];
  const PRIVACY = ["Use my full name","Use my first name only","I would like to discuss anonymity","I am comfortable appearing on video","I prefer audio only","I am comfortable with promotional clips","I would like more information before deciding"];
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] font-ec-b focus:border-[#D4AF37] focus:outline-none transition-colors";
  const lbl = "font-ec-b text-xs tracking-[0.15em] uppercase text-zinc-400 block mb-1.5";

  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-guest-form">
      <div className="max-w-3xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
          <h2 className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-4">Request to Take the Chair</h2>
          <p className="font-ec-b text-sm text-zinc-400">You do not need a perfect résumé. You do not need a large following. You do not need to be famous.<br/>You only need an honest story and a willingness to share it.</p>
        </motion.div>

        <form onSubmit={submit} className="space-y-8">
          {/* BASIC INFO */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Basic Information</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Full Legal Name *</label><input required value={form.full_name || ""} onChange={e => u("full_name", e.target.value)} className={inp} data-testid="guest-full-name" /></div>
              <div><label className={lbl}>Preferred Name</label><input value={form.preferred_name || ""} onChange={e => u("preferred_name", e.target.value)} className={inp} /></div>
              <div><label className={lbl}>Email *</label><input required type="email" value={form.email || ""} onChange={e => u("email", e.target.value)} className={inp} data-testid="guest-email" /></div>
              <div><label className={lbl}>Phone</label><input value={form.phone || ""} onChange={e => u("phone", e.target.value)} className={inp} /></div>
              <div><label className={lbl}>City</label><input value={form.city || ""} onChange={e => u("city", e.target.value)} className={inp} /></div>
              <div><label className={lbl}>State</label><input value={form.state || ""} onChange={e => u("state", e.target.value)} className={inp} /></div>
              <div><label className={lbl}>Country</label><input value={form.country || ""} onChange={e => u("country", e.target.value)} className={inp} /></div>
              <div><label className={lbl}>Pronouns (optional)</label><input value={form.pronouns || ""} onChange={e => u("pronouns", e.target.value)} className={inp} /></div>
            </div>
            <div><label className={lbl}>Website (optional)</label><input value={form.website || ""} onChange={e => u("website", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Social Media Links (optional)</label><input value={form.social_links || ""} onChange={e => u("social_links", e.target.value)} className={inp} placeholder="Instagram, LinkedIn, etc." /></div>
            <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={form.age_confirm || false} onChange={e => u("age_confirm", e.target.checked)} className="accent-[#D4AF37]" data-testid="guest-age-confirm" /><span className="font-ec-b text-sm text-zinc-300">I am 18 years of age or older *</span></label>
          </div>

          {/* YOUR STORY */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Your Story</p>
            <div><label className={lbl}>What is the story you would like to share? *</label><textarea required rows={4} value={form.story_share || ""} onChange={e => u("story_share", e.target.value)} className={inp + " resize-none"} data-testid="guest-story" /></div>
            <div><label className={lbl}>Why do you believe this conversation matters?</label><textarea rows={3} value={form.why_matters || ""} onChange={e => u("why_matters", e.target.value)} className={inp + " resize-none"} /></div>
            <div><label className={lbl}>What do you hope listeners take away?</label><textarea rows={2} value={form.listener_takeaway || ""} onChange={e => u("listener_takeaway", e.target.value)} className={inp + " resize-none"} /></div>
            <div><label className={lbl}>What has changed in your life because of this experience?</label><textarea rows={2} value={form.what_changed || ""} onChange={e => u("what_changed", e.target.value)} className={inp + " resize-none"} /></div>
            <div><label className={lbl}>Have you shared this story publicly before?</label><input value={form.shared_publicly || ""} onChange={e => u("shared_publicly", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Are there any topics you do not want discussed?</label><textarea rows={2} value={form.off_limits || ""} onChange={e => u("off_limits", e.target.value)} className={inp + " resize-none"} /></div>
            <div><label className={lbl}>Are there people, organizations, or legal matters requiring special care?</label><textarea rows={2} value={form.special_care || ""} onChange={e => u("special_care", e.target.value)} className={inp + " resize-none"} /></div>
            <div><label className={lbl}>Does your story involve allegations against an identifiable person or organization?</label>
              <select value={form.allegations || ""} onChange={e => u("allegations", e.target.value)} className={inp}>
                <option value="">Select...</option><option value="no">No</option><option value="yes">Yes</option>
              </select>
            </div>
            {form.allegations === "yes" && <div><label className={lbl}>If yes, please explain</label><textarea rows={2} value={form.allegations_detail || ""} onChange={e => u("allegations_detail", e.target.value)} className={inp + " resize-none"} /></div>}
          </div>

          {/* TOPICS */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-3">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Topic Selection</p>
            <div className="flex flex-wrap gap-2">
              {TOPICS.map(t => (
                <button key={t} type="button" onClick={() => toggleArr("topics", t)}
                  className={`px-3 py-1.5 rounded-full font-ec-b text-xs transition-all ${(form.topics || []).includes(t) ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500 hover:border-[#D4AF37]/30"}`}>{t}</button>
              ))}
            </div>
          </div>

          {/* RECORDING & PRIVACY */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-3">
              <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Recording Preference</p>
              {["In-person","Remote video","Remote audio only","No preference"].map(opt => (
                <label key={opt} className="flex items-center gap-3 cursor-pointer"><input type="radio" name="recording_pref" value={opt} checked={form.recording_pref === opt} onChange={() => u("recording_pref", opt)} className="accent-[#D4AF37]" /><span className="font-ec-b text-sm text-zinc-300">{opt}</span></label>
              ))}
            </div>
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-3">
              <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Privacy Preferences</p>
              {PRIVACY.map(opt => (
                <label key={opt} className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={(form.privacy_prefs || []).includes(opt)} onChange={() => toggleArr("privacy_prefs", opt)} className="accent-[#D4AF37]" /><span className="font-ec-b text-sm text-zinc-300">{opt}</span></label>
              ))}
            </div>
          </div>

          {/* AVAILABILITY */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Availability</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><label className={lbl}>Preferred Days</label><input value={form.availability_days || ""} onChange={e => u("availability_days", e.target.value)} className={inp} placeholder="e.g. Weekdays" /></div>
              <div><label className={lbl}>Preferred Times</label><input value={form.availability_times || ""} onChange={e => u("availability_times", e.target.value)} className={inp} placeholder="e.g. Mornings" /></div>
              <div><label className={lbl}>Time Zone</label><input value={form.timezone || ""} onChange={e => u("timezone", e.target.value)} className={inp} placeholder="e.g. Eastern" /></div>
            </div>
          </div>

          {/* MEDIA */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Media Experience</p>
            <div><label className={lbl}>Have you been interviewed before?</label><input value={form.interviewed_before || ""} onChange={e => u("interviewed_before", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Link to audio/video sample (optional)</label><input value={form.media_sample || ""} onChange={e => u("media_sample", e.target.value)} className={inp} placeholder="YouTube, podcast link, etc." /></div>
          </div>

          {/* CRISIS */}
          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.03] p-6 space-y-3">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-amber-400">Safety Question</p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.crisis_flag || false} onChange={e => { u("crisis_flag", e.target.checked); if (e.target.checked) setShowCrisisMsg(true); }} className="accent-amber-400 mt-1" />
              <span className="font-ec-b text-sm text-zinc-300">Is this story connected to a current crisis, immediate danger, active self-harm concern, or ongoing emergency?</span>
            </label>
            {showCrisisMsg && (
              <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-4">
                <p className="font-ec-b text-sm text-red-300"><AlertTriangle size={14} className="inline mr-1" />The Empty Chair™ is not an emergency or crisis service. If you or someone else is in immediate danger, contact emergency services or an appropriate crisis resource now.</p>
              </div>
            )}
          </div>

          {/* AGREEMENTS */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-3">
            <p className="font-ec-b text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Final Agreements</p>
            {[
              ["not_guaranteed", "I understand submitting this form does not guarantee selection."],
              ["not_therapy", "I understand this is a podcast request, not a request for therapy or crisis support."],
              ["may_contact", "I understand I may be contacted for more information."],
              ["info_accurate", "I confirm the information I provided is accurate to the best of my knowledge."],
              ["privacy_policy", "I agree to the website privacy policy."],
              ["release_willing", "I am willing to review and sign a separate guest participation and media release before recording."],
            ].map(([k, text]) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={(form.agreements || {})[k] || false} onChange={() => toggleAgreement(k)} className="accent-[#D4AF37] mt-1" data-testid={`agree-${k}`} />
                <span className="font-ec-b text-sm text-zinc-300">{text}</span>
              </label>
            ))}
            <div className="pt-4 border-t border-white/[0.06]">
              <label className={lbl}>Type Your Full Name *</label>
              <input required value={form.typed_name || ""} onChange={e => u("typed_name", e.target.value)}
                className="w-full bg-transparent border-b border-white/10 text-[#F5F5F0] py-3 focus:border-[#D4AF37] focus:outline-none transition-colors text-xl"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
                data-testid="guest-typed-name" />
            </div>
          </div>

          <button type="submit" disabled={submitting} data-testid="guest-submit"
            className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-full font-ec-b tracking-wide uppercase text-sm flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            {submitting ? "Submitting..." : "Request to Take the Chair"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ═══ FAQ ═══ */
function FaqSection({ faq }) {
  const [open, setOpen] = useState(null);
  if (!faq.length) return null;
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-faq">
      <div className="max-w-3xl mx-auto">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] text-center mb-16">Frequently Asked Questions</motion.h2>
        <div className="space-y-2">
          {faq.map((f, i) => (
            <div key={f.id} className="rounded-xl border border-white/[0.06] overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-ec-b text-sm text-[#F5F5F0] pr-4">{f.question}</span>
                <ChevronDown size={16} className={`text-[#D4AF37] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <div className="px-5 pb-5"><p className="font-ec-b text-sm text-zinc-400 leading-relaxed">{f.answer}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══ RESOURCES ═══ */
function Resources({ d }) {
  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-resources">
      <div className="max-w-5xl mx-auto">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] text-center mb-12">{d.resources_heading}</motion.h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(d.resources || []).map((r, i) => (
            <Link key={i} to={r.link || "#"} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-[#D4AF37]/30 transition-all group">
              <h3 className="font-ec-h text-lg text-[#D4AF37] mb-2 group-hover:text-[#F5F5F0] transition-colors">{r.title}</h3>
              <p className="font-ec-b text-xs text-zinc-400 leading-relaxed">{r.copy}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══ NEWSLETTER ═══ */
function Newsletter({ d }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const sub = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    try {
      await http.post("/empty-chair/public/newsletter", { email, name });
      toast.success("Subscribed!");
      setEmail(""); setName("");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-newsletter">
      <div className="max-w-xl mx-auto text-center">
        <h2 className="font-ec-h text-3xl text-[#F5F5F0] mb-4">{d.newsletter_heading}</h2>
        <p className="font-ec-b text-sm text-zinc-400 mb-8">{d.newsletter_copy}</p>
        <form onSubmit={sub} className="flex flex-col sm:flex-row gap-3">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="First name" className="flex-1 bg-transparent border border-white/10 rounded-full px-5 py-3 font-ec-b text-sm text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none" />
          <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" className="flex-1 bg-transparent border border-white/10 rounded-full px-5 py-3 font-ec-b text-sm text-[#F5F5F0] focus:border-[#D4AF37] focus:outline-none" />
          <button type="submit" disabled={saving} className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-ec-b text-sm tracking-wide uppercase hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50">{saving ? "..." : d.newsletter_btn}</button>
        </form>
      </div>
    </section>
  );
}

/* ═══ SAFETY ═══ */
function SafetyNotice({ d }) {
  return (
    <section className="bg-[#0D0D0D] py-16 px-6" data-testid="ec-safety">
      <div className="max-w-3xl mx-auto rounded-xl border border-amber-500/20 bg-amber-500/[0.03] p-6 flex gap-4">
        <AlertTriangle size={20} className="text-amber-400 shrink-0 mt-0.5" />
        <p className="font-ec-b text-xs text-zinc-400 leading-relaxed whitespace-pre-line">{d.safety_notice}</p>
      </div>
    </section>
  );
}

/* ═══ CONTACT ═══ */
function Contact({ d }) {
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-contact">
      <div className="max-w-2xl mx-auto text-center">
        <h2 className="font-ec-h text-3xl text-[#F5F5F0] mb-8">{d.contact_heading}</h2>
        <div className="space-y-3">
          <p className="font-ec-b text-base text-[#F5F5F0]">{d.contact_name}</p>
          <div className="flex flex-wrap gap-4 justify-center text-sm text-zinc-400">
            <a href={`mailto:${d.contact_email}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors"><Mail size={14} /> {d.contact_email}</a>
            <a href={`tel:${d.contact_phone}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors"><Phone size={14} /> {d.contact_phone}</a>
            <a href={`https://${d.contact_website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors"><Globe size={14} /> {d.contact_website}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ TESTIMONIALS ═══ */
function Testimonials({ testimonials }) {
  if (!testimonials.length) return null;
  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-testimonials">
      <div className="max-w-4xl mx-auto">
        <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="font-ec-h text-3xl md:text-5xl font-light text-[#F5F5F0] text-center mb-16">What People Are Saying</motion.h2>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-6">
          {testimonials.map(t => (
            <motion.div key={t.id} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
              <p className="font-ec-h text-base text-zinc-300 italic leading-relaxed mb-4">"{t.text}"</p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><span className="font-ec-b text-xs text-[#D4AF37]">{(t.name || "A")[0].toUpperCase()}</span></div>
                <div>
                  <p className="font-ec-b text-sm text-[#F5F5F0]">{t.name}</p>
                  {t.episode_title && <p className="font-ec-b text-[10px] text-zinc-500">on "{t.episode_title}"</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══ SHARE YOUR RESPONSE ═══ */
function ShareResponse({ episodes }) {
  const [form, setForm] = useState({ name: "", email: "", episode_id: "", episode_title: "", text: "", anonymous: false, permission_to_share: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) { toast.error("Please write a response"); return; }
    setSubmitting(true);
    try {
      await http.post("/empty-chair/public/testimonial", { ...form, type: "listener" });
      setDone(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <section className="bg-[#0D0D0D] py-20 px-6 text-center" data-testid="ec-share-success">
      <Check size={32} className="text-[#D4AF37] mx-auto mb-4" />
      <p className="font-ec-h text-xl text-[#F5F5F0]">Thank you for sharing.</p>
      <p className="font-ec-b text-sm text-zinc-500 mt-2">Your response will be reviewed before publishing.</p>
    </section>
  );

  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] font-ec-b focus:border-[#D4AF37] focus:outline-none transition-colors";

  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32 px-6" data-testid="ec-share-response">
      <div className="max-w-2xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
          <h2 className="font-ec-h text-3xl text-[#F5F5F0] mb-3">What did this conversation make you think about?</h2>
          <p className="font-ec-b text-sm text-zinc-500">Share your thoughts on an episode. Approved responses may appear publicly.</p>
        </motion.div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={form.name} onChange={e => u("name", e.target.value)} placeholder="Your name" className={inp} disabled={form.anonymous} data-testid="share-name" />
            <input type="email" value={form.email} onChange={e => u("email", e.target.value)} placeholder="Email (not displayed)" className={inp} />
          </div>
          <select value={form.episode_id} onChange={e => { const ep = episodes.find(x => x.id === e.target.value); u("episode_id", e.target.value); u("episode_title", ep?.title || ""); }} className={`${inp} bg-[#0A0A0A]`}>
            <option value="">Select an episode (optional)</option>
            {episodes.map(ep => <option key={ep.id} value={ep.id}>EP {ep.episode_number} — {ep.title}</option>)}
          </select>
          <textarea required rows={4} value={form.text} onChange={e => u("text", e.target.value)} placeholder="What did this conversation make you think about?" className={inp + " resize-none"} data-testid="share-text" />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 font-ec-b text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={form.anonymous} onChange={e => u("anonymous", e.target.checked)} className="accent-[#D4AF37]" /> Submit anonymously</label>
            <label className="flex items-center gap-2 font-ec-b text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={form.permission_to_share} onChange={e => u("permission_to_share", e.target.checked)} className="accent-[#D4AF37]" /> I give permission to share publicly</label>
          </div>
          <button type="submit" disabled={submitting} data-testid="share-submit"
            className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-ec-b text-sm tracking-wide uppercase hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50">
            {submitting ? "Submitting..." : "Share Your Response"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ═══ MEDIA INQUIRY ═══ */
function MediaInquiry({ d }) {
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", inquiry_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const TYPES = ["Podcast interview", "Guest recommendation", "Sponsorship", "Advertising", "Press inquiry", "Cross-promotion", "Venue partnership", "Production partnership", "Other"];

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await http.post("/empty-chair/public/media-inquiry", form);
      setDone(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  if (done) return (
    <section className="bg-[#0A0A0A] py-20 px-6 text-center" data-testid="ec-inquiry-success">
      <Check size={32} className="text-[#D4AF37] mx-auto mb-4" />
      <p className="font-ec-h text-xl text-[#F5F5F0]">Inquiry received.</p>
      <p className="font-ec-b text-sm text-zinc-500 mt-2">We will be in touch.</p>
    </section>
  );

  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F0] font-ec-b focus:border-[#D4AF37] focus:outline-none transition-colors";

  return (
    <section className="bg-[#0A0A0A] py-24 md:py-32 px-6" data-testid="ec-media-inquiry">
      <div className="max-w-2xl mx-auto">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-10">
          <h2 className="font-ec-h text-3xl text-[#F5F5F0] mb-3">{d.contact_heading || "Media, Sponsorship, and Collaboration"}</h2>
          <p className="font-ec-b text-sm text-zinc-500">For podcast interviews, sponsorship, press, and partnership inquiries.</p>
        </motion.div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={e => u("name", e.target.value)} placeholder="Your name *" className={inp} data-testid="inquiry-name" />
            <input value={form.organization} onChange={e => u("organization", e.target.value)} placeholder="Organization" className={inp} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required type="email" value={form.email} onChange={e => u("email", e.target.value)} placeholder="Email *" className={inp} data-testid="inquiry-email" />
            <input value={form.phone} onChange={e => u("phone", e.target.value)} placeholder="Phone" className={inp} />
          </div>
          <select required value={form.inquiry_type} onChange={e => u("inquiry_type", e.target.value)} className={`${inp} bg-[#0A0A0A]`} data-testid="inquiry-type">
            <option value="">Inquiry type *</option>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <textarea required rows={4} value={form.message} onChange={e => u("message", e.target.value)} placeholder="Your message *" className={inp + " resize-none"} data-testid="inquiry-message" />
          <button type="submit" disabled={submitting} data-testid="inquiry-submit"
            className="bg-[#D4AF37] text-[#0A0A0A] px-6 py-3 rounded-full font-ec-b text-sm tracking-wide uppercase hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all disabled:opacity-50">
            {submitting ? "Sending..." : "Send Inquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* ═══ FOOTER CTA ═══ */
function FooterCTA({ d, scrollToForm }) {
  return (
    <section className="bg-[#0D0D0D] py-28 md:py-36 px-6 text-center" data-testid="ec-footer-cta">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-ec-h text-4xl md:text-6xl font-light text-[#F5F5F0] mb-4">{d.footer_headline}</h2>
        <p className="font-ec-h text-xl text-zinc-400 italic mb-12">{d.footer_copy}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <button onClick={scrollToForm} className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 rounded-full font-ec-b tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all">Request to Take the Chair</button>
          <a href="#episodes" className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 rounded-full font-ec-b tracking-wide uppercase text-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Listen to the Latest Episode</a>
        </div>
        <p className="font-ec-h text-lg text-zinc-500 italic whitespace-pre-line">{d.footer_tagline}</p>
      </div>
    </section>
  );
}
